import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { GlobalService } from '@core/services/global/global.service';
import { environment } from '@env/environment';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GroupMember } from '@interface/Dealer';
import { FileuploadService } from '@core/services/filedownloadservice/fileupload.service';
import { PopupComponent } from '@shared/components/popup/popup.component';
import { CustomDropdownComponent } from '@shared/components/custom-dropdown/custom-dropdown.component';
import { FormsModule } from '@angular/forms';
import { NadaSpinnerComponent } from '@shared/components/nada-spinner/nada-spinner.component';
import { IDMS, IFileType, ISource } from '../../shared/constants/manual-entry';
import { CustomDateSelectComponent } from '../../shared/components/custom-date-select/custom-date-select.component';
import { AuthenticationService } from '../../core/services/Auth/authservice';

@Component({
  selector: 'app-statement-uploads',
  standalone: true,
  imports: [
    CommonModule,
    PopupComponent,
    CustomDropdownComponent,
    FormsModule,
    NadaSpinnerComponent,
    CustomDateSelectComponent,
  ],
  templateUrl: './statement-uploads.component.html',
  styleUrl: './statement-uploads.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class StatementUploadsComponent implements OnInit, OnDestroy {
  selectedDropDownValue: any;
  fileName: string = '';
  fileUploaded = false;
  uploadProgress = 0;
  private readonly unsubscribe$ = new Subject<void>();
  groupId: number | null = null;
  selectedDealer: GroupMember | null = null;
  showModalSpinner: boolean = false;
  showErrorModal: boolean = false;
  showSuccessModal: boolean = false;
  modalMessage: string = '';
  subscription?: Subscription;
  GroupName: string | null = null;
  allFileTypes: IFileType[] = [];
  allDMS: IDMS[] = [];
  allSource: ISource[] = [];
  dms: IDMS | null = null;
  otherDmsName: string = '';
  uploadedFileType: string = '';

  maxDate: Date = new Date();
  minDate: Date = new Date();
  statementDate: Date | null = new Date();
  authUser: any = null;
  selectedFile: File | null = null;
  constructor(
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef,
    private fileUploadService: FileuploadService,
    private authService: AuthenticationService,
  ) {}

  public ngOnInit() {
    const today = new Date();
    this.maxDate = new Date(today.getFullYear(), today.getMonth(), -1, 1);
    this.minDate = new Date(today.getFullYear() - 1, 0, 1);
    this.statementDate = new Date(today.getFullYear(), today.getMonth(), -1, 1);
    this.globalService.selectedDealer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (data: GroupMember | null) => {
        this.selectedDealer = data || null;
      });
    this.globalService.groupName$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((val) => {
        this.GroupName = val;
      });
    this.authService.fincomData$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.authUser = state;
      });
    this.getDMSData();
  }

  async getDMSData() {
    const response: any = await this.globalService.getApiDataWithReturn(
      environment.getDmsData,
    );
    if (response && response.data?.dms) this.allDMS = response.data.dms;
    else this.allDMS = [];

    if (response && response.data?.dmS_Source_Types)
      this.allSource = response.data.dmS_Source_Types;
    else this.allSource = [];

    if (response && response.data?.dmS_File_Types)
      this.allFileTypes = response.data.dmS_File_Types;
    else this.allFileTypes = [];
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (this.validateFile(file)) {
      this.selectedFile = file;
      this.simulateUpload(file);
    }

    // Reset the file input so the same file can be selected again
    event.target.value = null;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file && this.validateFile(file)) {
      this.selectedFile = file;
      this.simulateUpload(file);
    }
  }

  validateFile(file: File): boolean {
    const allowedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
    ];
    const allowedExtensions = ['xls', 'xlsx', 'xlsm'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > 10 * 1024 * 1024) {
      this.showErrorModal = true;
      this.modalMessage = 'File size exceeds 10MB limit';
      return false;
    }
    if (
      !allowedMimeTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension || '')
    ) {
      this.showErrorModal = true;
      this.modalMessage = 'Only Excel files (.xls, .xlsx, .xlsm) are allowed';
      return false;
    }
    this.uploadedFileType = 'Excel';
    return true;
  }

  simulateUpload(file: File) {
    this.fileName = file.name;
    this.fileUploaded = true;
    this.uploadProgress = 0;
    const totalSize = file.size; // bytes
    let uploaded = 0;

    const interval = setInterval(() => {
      uploaded += totalSize / 20; // simulate chunks
      this.uploadProgress = Math.min(
        Math.round((uploaded / totalSize) * 100),
        100,
      );

      if (this.uploadProgress === 100) {
        clearInterval(interval);
      }
    }, 150);
  }

  uploadFile() {
    if (!this.fileUploaded || !this.selectedDealer) {
      this.showErrorModal = true;
      this.modalMessage = 'Please upload a file.';
      return;
    }
    if (!this.selectedDealer || !this.statementDate) {
      this.showErrorModal = true;
      this.modalMessage = 'Please select date and month.';
      return;
    }
    if (!this.selectedDealer || !this.dms) {
      this.showErrorModal = true;
      this.modalMessage = 'Please select a DMS.';
      return;
    }
    if (this.dms?.name.toLowerCase() === 'other' && !this.otherDmsName) {
      this.showErrorModal = true;
      this.modalMessage = 'Please enter the DMS name.';
      return;
    }

    this.showModalSpinner = true;
    this.modalMessage = '';
    this.cdr.detectChanges();

    const formData = new FormData();
    const year = this.statementDate.getFullYear() || '';
    const month = this.statementDate.getMonth() + 1;

    const today = new Date();
    const OEMRecDate = `${today?.getMonth() + 1}/${today.getDate()}/${today.getFullYear()} ${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
    const fileType = this.allFileTypes.find(
      (ft) => ft.fileType === this.uploadedFileType,
    );
    const sourceType = this.allSource.find(
      (st) => st.sourceType === 'App Upload',
    );

    formData.append('GroupMember', this.selectedDealer?.groupMemberCode);
    formData.append('user', this.authUser?.email || '');
    formData.append('year', year.toString());
    formData.append('stmt_month', month.toString());
    formData.append('OEM_rec_date', OEMRecDate);
    formData.append('rec_file_type', fileType?.id.toString() || '');
    formData.append('dms', this.getDmsName());
    formData.append('source', sourceType?.id.toString() || '');
    formData.append('formFile', this.selectedFile!);
    const isLocalhost = window.location.hostname === 'localhost';
    let apiUrl = `${environment.statementupload}`;
    if (isLocalhost) {
      apiUrl += `?code=${environment.functionappCode}`;
    }
    this.fileUploadService.uploadFile(apiUrl, formData).subscribe({
      next: (response: any) => {
        this.showModalSpinner = false;
        if (response?.success) {
          this.showSuccessModal = true;
          this.modalMessage = response.message || 'File uploaded successfully!';
          this.sendAlert();
          this.removeFile();
        } else {
          this.showErrorModal = true;
          this.modalMessage =
            response.message || 'File Not uploaded successfully!';
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.showModalSpinner = false;
        this.showErrorModal = true;
        this.modalMessage = error.message || 'File Not uploaded successfully!';
        this.cdr.detectChanges();
      },
    });
  }

  sendAlert() {
    if (!this.statementDate) return;
    const today = new Date();
    const year = this.statementDate?.getFullYear();
    const month = this.statementDate?.getMonth() + 1;
    this.globalService.sendMailToAnalyst({
      uploadFrom: this.dms?.name,
      stmtDate: `${month}/${today.getDate()}/${year}`,
      user: this.authUser?.email || '',
    });
  }

  getDmsName(): string {
    return this.dms?.name.toLowerCase() === 'other'
      ? this.otherDmsName
      : this.dms?.name || '';
  }

  removeFile() {
    this.fileUploaded = false;
    this.fileName = '';
    this.uploadProgress = 0;
  }

  openFileInput(event: Event) {
    event.preventDefault();
    document.getElementById('fileInput')?.click();
  }

  get progressColor(): string {
    const progress = this.uploadProgress;
    return progress >= 100
      ? '#70BB21'
      : progress >= 75
        ? '#B7F9B3'
        : progress >= 50
          ? '#ECFFD8'
          : '#F9BCB3';
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
    this.subscription?.unsubscribe();
  }
}
